/* eslint-disable max-lines */
import moment from 'moment';
import { dateToAbsolute, durationStringToTuple, durationToMilliseconds, isDurationString, } from '../date-time';
import { isNumeric } from '../utilities';
import { MILLISECONDS_PER_SECOND, msToSeconds, SECONDS_PER_DAY, SECONDS_PER_HOUR, SECONDS_PER_MINUTE, secondsToDays, secondsToHours, secondsToMinutes, } from '../time-units.util';
import { bucketMap, dayInMs } from './interval-calculator.config';
const second = 1;
const minute = 60 * second;
const hour = 60 * minute;
const day = 24 * hour;
export const intervalData = [
    [1, 'm'],
    [2, 'm'],
    [3, 'm'],
    [5, 'm'],
    [10, 'm'],
    [15, 'm'],
    [30, 'm'],
    [1, 'h'],
    [2, 'h'],
    [3, 'h'],
    [4, 'h'],
    [8, 'h'],
    [12, 'h'],
    [24, 'h'],
    [48, 'h'],
    [1, 'w'],
    [30, 'd'],
    [60, 'd'],
];
const stepAsDuration = ({ start, end, size = 60, }) => {
    const step = (dateToAbsolute(end) - dateToAbsolute(start)) / size;
    return intervalData.find((data, index) => step < moment.duration(...intervalData[index + 1]).asSeconds()) || [];
};
const step = ({ start, end, size = 60 }) => stepAsDuration({ start, end, size }).join('');
// Based on this calc
// Step\start time  |  <=7d |  <=30d |  >30d
// _________________________|________|_________
//                  |       |        |
// <=1m             |   1m  |   4m   |  40m
//                  |       |        |
// <=10m            |   4m  |   4m   |  40m
//                  |       |        |
// >10m             |   40m |   40m  |  40m
// Max (step=15s, min_int(15s,<=7d)=1m) =1m
const rateIntervalMapper = [
    { step: 1 * minute, start: 7 * day, rateInterval: 1 * minute },
    { step: 1 * minute, start: 30 * day, rateInterval: 4 * minute },
    { step: 1 * minute, rateInterval: 1 * minute }, // no start, i.e > 30
    { step: 10 * minute, start: 7 * day, rateInterval: 4 * minute },
    { step: 10 * minute, start: 30 * day, rateInterval: 4 * minute },
    { step: 10 * minute * day, rateInterval: 40 * minute },
    { start: 7 * minute * day, rateInterval: 40 * minute },
    { start: 30 * minute * day, rateInterval: 40 * minute },
    { rateInterval: 40 * minute },
];
const rate = ({ step, start, end }) => {
    const timeFrame = dateToAbsolute(end) - dateToAbsolute(start);
    const stepInSec = moment.duration(...(intervalData.find(item => step === `${item[0]}${item[1]}`) ?? [])).asSeconds();
    const { rateInterval } = rateIntervalMapper.find(item => (item.step && stepInSec <= item.step && item.start && timeFrame <= item.start) ||
        (item.step && stepInSec <= item.step && !item.start) ||
        (!item.step && item.start && timeFrame <= item.start) ||
        (!item.step && !item.start)) ?? {};
    return `${moment.duration(Math.max(stepInSec, rateInterval ?? 0)).asMinutes() * 1000}m`;
};
/** Formats a duration for OpenSearch date math (`now-<duration>`). Units must be whole numbers. */
const formatOpenSearchRelativeDuration = (durationMs) => {
    const roundedMs = Math.max(1, Math.ceil(durationMs));
    if (roundedMs < MILLISECONDS_PER_SECOND) {
        return `${roundedMs}ms`;
    }
    const totalSeconds = msToSeconds(roundedMs);
    if (totalSeconds < SECONDS_PER_MINUTE) {
        return `${totalSeconds}s`;
    }
    if (totalSeconds % SECONDS_PER_DAY === 0) {
        return `${secondsToDays(totalSeconds)}d`;
    }
    if (totalSeconds % SECONDS_PER_HOUR === 0) {
        return `${secondsToHours(totalSeconds)}h`;
    }
    if (totalSeconds % SECONDS_PER_MINUTE === 0) {
        return `${secondsToMinutes(totalSeconds)}m`;
    }
    return `${totalSeconds}s`;
};
const getDurationFromStartEnd = ({ start, end }) => {
    if (!start || !end)
        return null;
    const durationMs = moment(dateToAbsolute(end) * 1000).diff(moment(dateToAbsolute(start) * 1000));
    if (durationMs <= 0) {
        return null;
    }
    return formatOpenSearchRelativeDuration(durationMs);
};
const snapToFloor = ({ timestampInMs, bucketMapItem }) => {
    const { ms, type } = bucketMapItem;
    if (type === 'ms' || type === 's' || type === 'm' || type === 'h') {
        return new Date(Math.floor(timestampInMs / ms) * ms).toISOString();
    }
    if (type === 'd') {
        return new Date(Math.floor(timestampInMs / dayInMs) * dayInMs).toISOString();
    }
    const date = new Date(timestampInMs);
    if (type === 'w') {
        const daysFromMonday = (date.getUTCDay() + 6) % 7;
        date.setUTCDate(date.getUTCDate() - daysFromMonday);
        date.setUTCHours(0, 0, 0, 0);
        return date.toISOString();
    }
    if (type === 'M') {
        date.setUTCDate(1);
        date.setUTCHours(0, 0, 0, 0);
        return date.toISOString();
    }
    if (type === 'y') {
        date.setUTCMonth(0, 1);
        date.setUTCHours(0, 0, 0, 0);
        return date.toISOString();
    }
    return new Date(Math.floor(timestampInMs / ms) * ms).toISOString();
};
const offsetIso = (iso, offsetMs) => new Date(new Date(iso).getTime() + offsetMs).toISOString();
const calculateAutoBucket = ({ start, end, maxBuckets }) => {
    const diff = end - start;
    const bucketSizeMs = diff / maxBuckets;
    let lowerBound, upperBound = bucketMap[0];
    bucketMap.forEach(b => {
        if (bucketSizeMs >= b.ms) {
            upperBound = b;
        }
        if (lowerBound === undefined && bucketSizeMs < b.ms) {
            lowerBound = b;
        }
    });
    if (lowerBound === undefined) {
        lowerBound = upperBound;
    }
    const upperBoundBuckets = Math.round(diff / upperBound.ms);
    const lowerBoundBuckets = Math.round(diff / lowerBound.ms);
    // here we decide which of 2 value(lowerBound, upperBound) have less distance to maxBuckets
    const desiredBucket = maxBuckets - lowerBoundBuckets <= upperBoundBuckets - maxBuckets ? lowerBound : upperBound;
    return desiredBucket;
};
const calculateIntervalBucket = ({ start, end, intervalType, maxBuckets }) => {
    const diff = end - start;
    const bucketSizeMs = diff / maxBuckets;
    const desiredBucket = bucketMap.find(b => b.type === intervalType);
    if (desiredBucket && bucketSizeMs <= desiredBucket.ms) {
        return desiredBucket;
    }
    return calculateAutoBucket({ start, end, maxBuckets });
};
/**
 * findBucket time interval for histogram
 * @param start timestamp in milliseconds
 * @param end timestamp in milliseconds
 * @param maxBuckets desired number of buckets
 * @param intervalType desired interval size (auto, ms, s, m, h, w, M, y)
 */
const findBucket = ({ start, end, intervalType, maxBuckets = 100 }) => {
    const isAutoInterval = intervalType === 'auto' || !intervalType;
    const desiredBucket = isAutoInterval
        ? calculateAutoBucket({ start, end, maxBuckets })
        : calculateIntervalBucket({ start, end, intervalType, maxBuckets });
    const intervalMs = desiredBucket.ms;
    const exceedsMaxBuckets = !isAutoInterval && Boolean(intervalType && desiredBucket.name !== `1${intervalType}`);
    const lowerBounds = {
        start: snapToFloor({ timestampInMs: start, bucketMapItem: desiredBucket }),
        end: snapToFloor({ timestampInMs: end, bucketMapItem: desiredBucket }),
    };
    return {
        upperBounds: {
            start: offsetIso(lowerBounds.start, -intervalMs),
            end: offsetIso(lowerBounds.end, intervalMs),
        },
        lowerBounds,
        interval: desiredBucket.name,
        intervalMs,
        exceedsMaxBuckets,
    };
};
const formatBucket = ({ start, end, interval, maxBuckets = 100, }) => {
    const trimmedInterval = interval.trim();
    const normalizedInterval = isNumeric(trimmedInterval) ? `${trimmedInterval}m` : trimmedInterval;
    if (!isDurationString(normalizedInterval))
        return findBucket({ start, end, intervalType: 'auto', maxBuckets });
    const intervalMs = durationToMilliseconds(normalizedInterval) ?? 0;
    const [, intervalType] = durationStringToTuple(normalizedInterval) ?? [, 'm'];
    const possibleAutoBucket = calculateAutoBucket({ start, end, maxBuckets });
    if (possibleAutoBucket.ms > intervalMs) {
        const autoInterval = findBucket({ start, end, intervalType: 'auto', maxBuckets });
        return {
            ...autoInterval,
            exceedsMaxBuckets: true,
        };
    }
    const bucketMapItem = { name: `1${intervalType}`, ms: intervalMs, type: intervalType };
    const lowerBounds = {
        start: snapToFloor({ timestampInMs: start, bucketMapItem }),
        end: snapToFloor({ timestampInMs: end, bucketMapItem }),
    };
    return {
        lowerBounds,
        upperBounds: {
            start: offsetIso(lowerBounds.start, -intervalMs),
            end: offsetIso(lowerBounds.end, intervalMs),
        },
        interval: normalizedInterval,
        intervalMs,
    };
};
export const intervalCalculatorService = {
    stepAsDuration,
    step,
    rate,
    getDurationFromStartEnd,
    findBucket,
    formatBucket,
};
//# sourceMappingURL=interval-calculator.service.js.map